import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';

import { Farm } from '../../models/farm.model';
import { FarmService } from './farm.service';

@Injectable({
  providedIn: 'root',
})
export class ActiveFarmService {
  private readonly farmService = inject(FarmService);

  readonly farms = signal<Farm[]>([]);
  readonly activeFarmId = signal<number | null>(null);

  readonly loading = signal(false);
  readonly error = signal(false);

  /**
   * HTTP status of the last failed load, or null when the request never
   * reached the server (network error, server down).
   */
  readonly errorStatus = signal<number | null>(null);

  readonly activeFarm = computed(() => {
    const activeFarmId = this.activeFarmId();

    if (activeFarmId === null) {
      return null;
    }

    return this.farms().find((farm) => farm.id === activeFarmId) ?? null;
  });

  loadFarms(): void {
    this.loading.set(true);
    this.error.set(false);
    this.errorStatus.set(null);

    this.farmService.getFarms().subscribe({
      next: (farms) => {
        this.farms.set(farms);

        const currentFarmId = this.activeFarmId();

        const currentFarmStillExists = farms.some(
          (farm) => farm.id === currentFarmId,
        );

        if (!currentFarmStillExists) {
          this.activeFarmId.set(farms[0]?.id ?? null);
        }

        this.loading.set(false);
      },
      error: (error: unknown) => {
        this.farms.set([]);
        this.activeFarmId.set(null);
        this.loading.set(false);
        this.error.set(true);
        this.errorStatus.set(
          error instanceof HttpErrorResponse ? error.status : null,
        );
      },
    });
  }

  selectFarm(farmId: number): void {
    const farmExists = this.farms().some((farm) => farm.id === farmId);

    if (!farmExists) {
      return;
    }

    this.activeFarmId.set(farmId);
  }
}
